import React from 'react'
import { Stepper, Step, StepLabel, Button, Card, Typography, TextField, Box, Divider, Stack, InputBase, InputLabel, FormControl, Grid, styled, alpha, Snackbar, Alert, IconButton, useMediaQuery } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker';
import dayjs from 'dayjs';
import {
    usePickerLayout,
    pickersLayoutClasses,
    PickersLayoutRoot,
    PickersLayoutContentWrapper,
} from '@mui/x-date-pickers/PickersLayout';
import { ArrowCircleLeft, ArrowCircleLeftOutlined, ArrowCircleLeftTwoTone, ArrowCircleRight, ArrowLeft, ArrowRight, ArrowRightAlt, CalendarToday, CalendarTodayOutlined, ChevronRight } from '@mui/icons-material';
import { format } from 'date-fns';


const BootstrapInput = styled(InputBase)(({ theme }) => ({
    width: '320px',
    'label + &': {
        marginTop: theme.spacing(3),
        color: '#000'
    },
    '& .MuiInputBase-input': {
        borderRadius: 4,
        position: 'relative',
        textAlign: 'start',
        backgroundColor: theme.palette.mode === 'light' ? '#F4F3F2' : '#1A2027',
        border: '1px solid',
        borderColor: theme.palette.mode === 'light' ? '#F4F3F2' : '#2D3843',
        fontSize: 16,
        width: '100%',
        height: '30px',
        padding: '5px 12px',
        transition: theme.transitions.create([
            'border-color',
            'background-color',
            'box-shadow',
        ]),
        // Use the system font instead of the default Roboto font.
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
        '&:focus': {
            boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
            borderColor: theme.palette.primary.main,
        },
    },
}));

const StyledInput = styled(InputLabel)({
    fontSize: '20px',
    color: '#000',
    fontWeight: 500
});

const steps = ['Select Date & Time', 'Client Information', 'Review'];



function CustomLayout(props) {
    const { toolbar, tabs, content, actionBar } = usePickerLayout(props);

    return (
        <PickersLayoutRoot
            ownerState={props}
            sx={{
                overflow: 'auto',
                fontSize: '1rem',
                [`.${pickersLayoutClasses.actionBar}`]: {
                    gridColumn: 1,
                    gridRow: 1,
                },
                [`.${pickersLayoutClasses.toolbar}`]: {
                    gridColumn: 2,
                    gridRow: 1,
                    backgroundColor: '#f0f0f0', // Example background color
                    padding: '20px', // Example padding
                    borderRadius: '8px', // Example border radius
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    height: '220px',
                    maxWidth: '320px',
                    display: 'none'
                },

            }}
        >
            {toolbar}
            {actionBar}
            <PickersLayoutContentWrapper className={pickersLayoutClasses.contentWrapper} sx={{

            }}>
                {tabs}
                {content}
            </PickersLayoutContentWrapper>
        </PickersLayoutRoot>
    );
}

const StepContent = ({ step, handleChange, values, handleNext, setActiveStep, isDateTimeSelected, handleSubmit, isSuccess, handleSnackbarClose }) => {
    const formatDateToWords = (date) => {
        return date ? dayjs(date).format('dddd, MMMM D, YYYY h:mm A') : '';
    };
    switch (step) {
        case 0:
            return (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="h7" sx={{ marginBottom: 1 }}>Select Date & Time</Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <StaticDateTimePicker
                                orientation="landscape"
                                defaultValue={dayjs('2022-04-17')}
                                slots={{
                                    layout: CustomLayout,
                                    actionBar: 'none',
                                }}
                                slotProps={{
                                    toolbar: { toolbarFormat: 'ddd DD MMMM', hidden: false },
                                }}
                                ampmInClock="true"
                                value={values.dateTime}
                                onChange={(newValue) => handleChange({ target: { name: 'dateTime', value: newValue } })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        sx={{
                                            m: 1,
                                            width: '100%',
                                            '& .MuiInputBase-root': {
                                                backgroundColor: '#fff',
                                                color: '#000', // Text color
                                            },
                                        }}
                                    />
                                )}
                                shouldDisableDate={(date) => date.isBefore(dayjs(), 'day')}
                                minDate={dayjs()}
                            />
                        </LocalizationProvider>

                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', m: '1em 0em' }}>
                        <Button onClick={handleNext}
                            disabled={!isDateTimeSelected() || (step === 0 && !values.dateTime)}
                            sx={{
                                width: '100px',
                                backgroundColor: '#978A7D',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'capitalize',
                                '&:hover': {
                                    background: '#444444',
                                    color: '#fff',
                                },
                                '&:disabled': {
                                    backgroundColor: '#d3d3d3', // Change this to your desired disabled color
                                    color: '#9e9e9e', // Change this to your desired disabled text color
                                },
                            }}
                            endIcon={<ArrowCircleRight />}
                        >
                            Next
                        </Button>
                    </Box>
                </>
            );
        case 1:
            return (
                <Box component="form" sx={{ '& .MuiTextField-root': { m: 1, width: 'auto' }, display: 'flex', flexDirection: 'column' }} noValidate autoComplete="off">
                    <Stack
                        direction="column"
                        justifyContent="center"
                        alignItems="flex-start"
                        spacing={2}
                        sx={{ pb: '1em' }}
                    >
                        <Stack
                            direction="row"
                            spacing={0}
                        >
                            <CalendarToday sx={{ p: '0px 5px' }} />
                            <Typography variant="h7">   {formatDateToWords(values.dateTime.format('MM/DD/YYYY, h:mm A'))}</Typography>
                        </Stack>
                    </Stack>
                    <Grid container spacing={2} sx={{ mt: '1em' }}>
                        <Grid item xs={12}>
                            <FormControl variant="standard">
                                <StyledInput shrink htmlFor="bootstrap-input" >Name</StyledInput>
                                <BootstrapInput
                                    label="Name"
                                    fullWidth
                                    name="name"
                                    type="text"
                                    required
                                    value={values.name}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl variant="standard">
                                <StyledInput shrink htmlFor="bootstrap-input" >Email</StyledInput>
                                <BootstrapInput
                                    label="Email"
                                    fullWidth
                                    name="email"
                                    type="text"
                                    required
                                    value={values.email}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl variant="standard">
                                <StyledInput shrink htmlFor="bootstrap-input" >Phone Number</StyledInput>
                                <BootstrapInput
                                    label="Phone Number"
                                    fullWidth
                                    name="phone"
                                    type="number"
                                    required
                                    value={values.phone}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl variant="standard">
                                <StyledInput shrink htmlFor="bootstrap-input" >Address</StyledInput>
                                <BootstrapInput
                                    label="Address"
                                    fullWidth
                                    name="address"
                                    type="text"
                                    required
                                    rows={2}
                                    value={values.address}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', mt: '3em' }}>
                        <Button onClick={handleSubmit}
                            sx={{
                                width: '180px',
                                backgroundColor: '#978A7D',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'capitalize',
                                '&:hover': {
                                    background: '#444444',
                                    color: '#fff',
                                },
                                '&:disabled': {
                                    backgroundColor: '#d3d3d3', // Change this to your desired disabled color
                                    color: '#9e9e9e', // Change this to your desired disabled text color
                                },
                            }}>
                            Schedule Event
                        </Button>
                    </Box>
                </Box>
            );
        default:
            return 'Unknown step';
    }
};

const DateTimeCard = ({close}) => {
    const matches = useMediaQuery('(max-width:600px)');
    const [activeStep, setActiveStep] = React.useState(0);
    const [formValues, setFormValues] = React.useState({
        dateTime: null,
        name: '',
        email: '',
        address: '',
        phone: '',
    });
    const [isSuccess, setIsSuccess] = React.useState(true);
    const [state, setState] = React.useState({
        open: false,
        vertical: 'top',
        horizontal: 'center',
    });

    const { vertical, horizontal, open, } = state;
    const isDateTimeSelected = () => formValues.dateTime !== null;

    const validateForm = () => {
        const { dateTime, name, email, address, phone } = formValues;

        if (!dateTime || !name || !email || !address || !phone) {
            // At least one field is empty
            return false;
        }

        // Your other validation logic here

        return true;
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleSnackbarClose = () => {
        setState({ open: false });
        close(false)
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        // Validate form before submitting
        if (validateForm()) {
            // Handle form submission 
            setIsSuccess(true);
            setState({ open: true, vertical: 'bottom', horizontal: 'right' });
            setFormValues({
                dateTime: null,
                name: '',
                email: '',
                address: '',
                phone: '',
            });
            setActiveStep(0);
            console.log('Alert', formValues)
        } else {
            setIsSuccess(false);
            setState({ open: true, vertical: 'bottom', horizontal: 'right' });
        }
    };



    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);
    const handleReset = () => setActiveStep(0);
    const handleChange = (e) => {
        setFormValues({
            ...formValues,
            [e.target.name]: e.target.value,
        });
    };



    return (
        <div>
            <Card
                elevation={1}
                orientation="vertical"
                variant="outlined"
                sx={{ width: matches ? 'auto' : '380px', height: 'auto', m: matches ? '1em 0' : '2em 0em', }}
            >
                <Box sx={{ width: '100%' }}>
                    <Typography variant='h4' sx={{ fontSize: matches ? '16px' : '19px', mt: '1em' }}>Schedule a Free Consultation</Typography>
                    {activeStep === 1 ?
                        <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'start' }}>
                            <IconButton onClick={handleBack} disableRipple>
                                <ArrowCircleLeftTwoTone sx={{
                                    fontSize: 40,
                                    '&:hover': {
                                        color: '#978A7D',
                                    },
                                }} />
                            </IconButton>
                        </Box>
                        :
                        null}
                    <Divider sx={{ mt: '1em' }} />
                    <div>
                        <Box sx={{
                            p: 3,
                            mt: matches ? '2em' : 0,
                            display: matches ? 'flex' : null,
                            flexDirection: matches ? 'column' : null,
                            justifyContent: matches ? 'center' : null,
                            alignItems: matches ? 'center' : null
                        }}>
                            <StepContent
                                step={activeStep}
                                handleChange={handleChange}
                                values={formValues}
                                setFormValues={setFormValues}
                                handleNext={handleNext}
                                setActiveStep={setActiveStep}
                                isDateTimeSelected={isDateTimeSelected}
                                handleSubmit={handleSubmit}
                                handleSnackbarClose={handleSnackbarClose}
                                isSuccess={isSuccess}
                            />
                            <Snackbar
                                open={open}
                                autoHideDuration={4000}
                                onClose={handleSnackbarClose}
                            >
                                <Alert
                                    onClose={handleSnackbarClose}
                                    severity={isSuccess ? 'success' : 'error'}
                                    variant="filled"
                                >
                                    {isSuccess
                                        ? 'Form submitted successfully!'
                                        : 'Please fill in all fields.'}
                                </Alert>
                            </Snackbar>
                        </Box>
                    </div>
                </Box>
            </Card>
        </div>
    )
}

export default DateTimeCard